/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
 AreaChart, Area, BarChart, Bar, XAxis, YAxis,
 Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import SvIkon from './SvIkon';
// ── CSS animationer ───────────────────────────────────────
const BI_CSS = `
@keyframes bi-fade-up {
 from { opacity: 0; transform: translateY(16px); }
 to { opacity: 1; transform: translateY(0); }
}
@keyframes bi-breathe {
 0%, 100% { opacity: 1; }
 50% { opacity: 0.75; }
}
@keyframes bi-bar-grow {
 from { transform: scaleX(0); }
 to { transform: scaleX(1); }
}
@keyframes bi-count {
 from { opacity: 0; transform: translateY(8px); }
 to { opacity: 1; transform: translateY(0); }
}
@keyframes bi-pulse-ring {
 0% { box-shadow: 0 0 0 0 rgba(63,212,199,0.35); }
 70% { box-shadow: 0 0 0 10px rgba(63,212,199,0); }
 100% { box-shadow: 0 0 0 0 rgba(63,212,199,0); }
}
@keyframes bi-skeleton {
 0% { background-position: -200% 0; }
 100% { background-position: 200% 0; }
}
@keyframes bi-tab-slide {
 from { opacity: 0; transform: translateX(12px); }
 to { opacity: 1; transform: translateX(0); }
}
`;
// ── Design tokens ─────────────────────────────────────────
const T = {
 surface: 'rgba(8,15,36,0.72)',
 surface2: 'rgba(6,12,28,0.88)',
 border: 'rgba(63,212,199,0.10)',
 cyan: '#3fd4c7',
 purple: '#8b5cf6',
 green: '#22c55e',
 amber: '#f59e0b',
 red: '#ef4444',
 blue: '#3b82f6',
 gold: '#c9a84c',
 text: '#dde8ff',
 text2: '#8a9ab8',
 text3: '#4a5a72',
 mono: '"JetBrains Mono","Fira Code",monospace',
 sans: '"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};
// ── API helper ─────────────────────────────────────────────
const apiFetch = async (path) => {
 const res = await fetch('/api' + path, {
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 });
 if (!res.ok) throw new Error(`HTTP ${res.status}`);
 return res.json();
};
// ── Formattering ──────────────────────────────────────────
const fmtKr = v => v != null ? `${Math.round(v).toLocaleString('da-DK')} kr` : '—';
const fmtNum = v => v != null ? Number(v).toLocaleString('da-DK') : '—';
const fmtPct = v => v != null ? `${Math.round(v)}%` : '—';
// ── Månedsnavn-mapping ────────────────────────────────────
const MAANED_NAVNE = {
 '01':'Jan','02':'Feb','03':'Mar','04':'Apr','05':'Maj','06':'Jun',
 '07':'Jul','08':'Aug','09':'Sep','10':'Okt','11':'Nov','12':'Dec',
};
// ── Skeleton ──────────────────────────────────────────────
function Skel({ h = 16, w = '100%', r = 6, mb = 0 }) {
 return (
 <div style={{
 height: h, width: w, borderRadius: r, marginBottom: mb,
 background: 'linear-gradient(90deg,rgba(63,212,199,0.04)0%,rgba(63,212,199,0.10)50%,rgba(63,212,199,0.04)100%)',
 backgroundSize: '200% 100%',
 animation: 'bi-skeleton 1.6s linear infinite',
 }} />
 );
}
// ── Panel container ───────────────────────────────────────
function Panel({ children, accent = T.cyan, style = {}, delay = 0 }) {
 return (
 <div style={{
 background: T.surface2,
 backdropFilter: 'blur(28px)',
 WebkitBackdropFilter: 'blur(28px)',
 border: `1px solid ${T.border}`,
 borderRadius: 20,
 padding: 'clamp(18px, 2.2vw, 28px)',
 position: 'relative',
 overflow: 'hidden',
 animation: `bi-fade-up 0.45s ease ${delay}s both`,
 ...style,
 }}> <div style={{
 position: 'absolute', top: 0, left: 0, right: 0, height: 2,
 background: `linear-gradient(90deg,transparent,${accent}70,transparent)`,
 }} />
 {children}
 </div>
 );
}
// ── KPI-kort ──────────────────────────────────────────────
function KPIKort({ label, value, farve = T.cyan, ikon, sub, trend, loading, delay = 0 }) {
 return (
 <div style={{
 background: T.surface,
 backdropFilter: 'blur(24px)',
 WebkitBackdropFilter: 'blur(24px)',
 border: `1px solid ${farve}18`,
 borderTop: `2px solid ${farve}`,
 borderRadius: 18,
 padding: 'clamp(16px, 2vw, 24px)',
 position: 'relative',
 overflow: 'hidden',
 animation: `bi-fade-up 0.5s ease ${delay}s both`,
 }}> <div style={{
 position: 'absolute', top: 2, left: 0, right: 0, height: 1,
 background: `linear-gradient(90deg,transparent,${farve}45,transparent)`,
 }} /> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}> <span style={{ fontSize: 11, color: T.text3, fontFamily: T.mono, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
 {label}
 </span> {ikon && typeof ikon === 'string' && ikon.match(/^[A-Z]/) ? <SvIkon navn={ikon} size={18} color={farve} /> : <span style={{ fontSize: 'clamp(16px, 1.8vw, 22px)' }}>{ikon}</span>} </div>
 {loading ? (
 <Skel h={42} w="55%" mb={10} />
 ) : (
 <div style={{
 fontSize: 'clamp(26px, 3.2vw, 44px)', fontWeight: 900, color: farve,
 lineHeight: 1, letterSpacing: '-0.02em',
 animation: 'bi-count 0.5s ease both',
 textShadow: `0 0 24px ${farve}30`,
 }}>
 {value ?? '—'}
 </div>
 )}
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
 {sub && <span style={{ fontSize: 11, color: T.text2 }}>{sub}</span>}
 {trend != null && isFinite(trend) && (
 <span style={{
 fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
 background: trend >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
 color: trend >= 0 ? T.green : T.red,
 }}>
 {trend >= 0 ? '↑' : '↓'} {Math.abs(Math.round(trend))}%
 </span>
 )}
 </div> </div>
 );
}
// ── Horisontalt søjlediagram (CSS-baseret) ────────────────
function HorisontalBar({ label, value, max, farve = T.cyan, total }) {
 const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
 return (
 <div style={{ marginBottom: 14 }}> <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}> <span style={{ fontSize: 12, color: T.text2, fontWeight: 500 }}>{label}</span> <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}> <span style={{ fontSize: 13, fontWeight: 800, color: farve }}>{value}</span>
 {total > 0 && <span style={{ fontSize: 10, color: T.text3 }}>{Math.round((value/total)*100)}%</span>}
 </div> </div> <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 6, height: 8, overflow: 'hidden' }}> <div style={{
 height: '100%', width: `${pct}%`, borderRadius: 6,
 background: `linear-gradient(90deg, ${farve}CC, ${farve})`,
 boxShadow: `0 0 8px ${farve}40`,
 transformOrigin: 'left',
 animation: 'bi-bar-grow 0.8s cubic-bezier(0.34,1.56,0.64,1) both',
 }} /> </div> </div>
 );
}
// ── Scenarie-progress ─────────────────────────────────────
function ScenarieBarre({ navn, interval, farve, faktisk, maal, ikon }) {
 const pct = maal > 0 ? Math.min(100, Math.round((faktisk / maal) * 100)) : 0;
 return (
 <div style={{
 background: `${farve}08`, border: `1px solid ${farve}18`,
 borderLeft: `3px solid ${farve}`, borderRadius: 14, padding: '16px 20px',
 }}> <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}> <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}> <span style={{ fontSize: 18 }}>{ikon}</span> <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{navn}</span> </div> <span style={{ fontWeight: 800, color: farve, fontSize: 16 }}>{interval}</span> </div> <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 6, height: 6, overflow: 'hidden' }}> <div style={{
 height: '100%', width: `${pct}%`, borderRadius: 6,
 background: `linear-gradient(90deg, ${farve}AA, ${farve})`,
 boxShadow: `0 0 6px ${farve}40`,
 animation: 'bi-bar-grow 1s cubic-bezier(0.34,1.56,0.64,1) both',
 }} /> </div> <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: T.text3 }}> <span>{faktisk > 0 ? `${Math.round(faktisk).toLocaleString('da-DK')} kr faktisk` : 'Afventer data'}</span> <span>{pct}% af mål</span> </div> </div>
 );
}
// ── Custom recharts tooltip ────────────────────────────────
function CustomTooltip({ active, payload, label, formatValue }) {
 if (!active || !payload?.length) return null;
 return (
 <div style={{
 background: T.surface2, border: `1px solid ${T.border}`,
 borderRadius: 10, padding: '10px 14px',
 backdropFilter: 'blur(20px)',
 }}> <p style={{ color: T.text2, fontSize: 11, margin: '0 0 6px', fontFamily: T.mono }}>{label}</p>
 {payload.map((p, i) => (
 <p key={i} style={{ color: p.color, fontSize: 13, fontWeight: 700, margin: '2px 0' }}>
 {p.name && <span style={{ color: T.text3, fontWeight: 400 }}>{p.name}: </span>}
 {formatValue ? formatValue(p.value) : p.value}
 </p>
 ))}
 </div>
 );
}
// ── Lønforpligtelse-ring ───────────────────────────────────
function LoenRing({ maaned, loensforpligtelse = 60000 }) {
 const dækket = maaned >= loensforpligtelse;
 const pct = Math.min(100, maaned > 0 ? (maaned / loensforpligtelse) * 100 : 0);
 const r = 40;
 const circ = 2 * Math.PI * r;
 const farve = dækket ? T.green : pct > 60 ? T.amber : T.red;
 return (
 <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}> <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}> <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}> <circle cx={50} cy={50} r={r} fill="none" stroke={`${farve}15`} strokeWidth={8} /> <circle cx={50} cy={50} r={r} fill="none" stroke={farve} strokeWidth={8}
 strokeDasharray={`${circ * pct / 100} ${circ}`} strokeLinecap="round"
 style={{ filter: `drop-shadow(0 0 6px ${farve})`, transition: 'stroke-dasharray 0.9s ease' }}
 /> </svg> <div style={{
 position: 'absolute', inset: 0,
 display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
 }}> <span style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 800, color: farve, lineHeight: 1 }}>
 {Math.round(pct)}%
 </span> <span style={{ fontFamily: T.mono, fontSize: 9, color: T.text3, marginTop: 2 }}>dækket</span> </div> </div> <div> <p style={{ color: T.text, fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>
 Månedlig lønforpligtelse
 </p> <p style={{ color: farve, fontSize: 'clamp(20px, 2.5vw, 28px)', fontWeight: 900, margin: '0 0 6px', lineHeight: 1 }}>
 {fmtKr(maaned)} <span style={{ fontSize: 13, fontWeight: 400, color: T.text2 }}>/ {fmtKr(loensforpligtelse)}</span> </p> <div style={{
 display: 'inline-flex', alignItems: 'center', gap: 6,
 background: dækket ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
 border: `1px solid ${dækket ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
 borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700,
 color: dækket ? T.green : T.red,
 }}>
 {dækket ? ' Forpligtelse dækket' : ' Ikke dækket endnu'}
 </div> </div> </div>
 );
}
// ── Fastholdelsesring (genanvendelig gauge) ───────────────
function GaugeRing({ pct, farve, label, sublabel }) {
 const r = 40;
 const circ = 2 * Math.PI * r;
 const safePct = Math.min(100, Math.max(0, pct || 0));
 return (
 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}> <div style={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}> <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}> <circle cx={50} cy={50} r={r} fill="none" stroke={`${farve}15`} strokeWidth={8} /> <circle cx={50} cy={50} r={r} fill="none" stroke={farve} strokeWidth={8}
 strokeDasharray={`${circ * safePct / 100} ${circ}`} strokeLinecap="round"
 style={{ filter: `drop-shadow(0 0 6px ${farve})`, transition: 'stroke-dasharray 0.9s ease' }}
 /> </svg> <div style={{
 position: 'absolute', inset: 0,
 display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
 }}> <span style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 800, color: farve, lineHeight: 1 }}>
 {Math.round(safePct)}%
 </span> <span style={{ fontFamily: T.mono, fontSize: 9, color: T.text3, marginTop: 2 }}>{sublabel}</span> </div> </div> <span style={{ fontSize: 12, color: T.text2, fontWeight: 600, textAlign: 'center' }}>{label}</span> </div>
 );
}
// ── Hoved-komponent ───────────────────────────────────────
export default function BIDashboard() {
 const [sagData, setSagData] = useState(null);
 const [oekData, setOekData] = useState(null);
 const [osData, setOsData] = useState(null);
 const [vaekstData, setVaekstData] = useState(null);
 const [beslutninger, setBeslutninger] = useState([]);
 const [loading, setLoading] = useState(true);
 const [sidst, setSidst] = useState(null);
 const [aktiv_fane, setAktiv_fane] = useState('okonomi');
 const hent = useCallback(async () => {
 try {
 const [s, o, os, v, b] = await Promise.all([
 apiFetch('/os/intern/statistik').catch(() => null),
 apiFetch('/os/intern/okonomi').catch(() => null),
 apiFetch('/os/statistik').catch(() => null),
 apiFetch('/os/intern/vaekst').catch(() => null),
 apiFetch('/os/beslutninger?status=AFVENTER').catch(() => ({ beslutninger: [] })),
 ]);
 if (s) setSagData(s);
 if (o) setOekData(o);
 if (os) setOsData(os);
 if (v) setVaekstData(v);
 setBeslutninger(b?.beslutninger || []);
 setSidst(new Date());
 } catch (err) {
 console.error('[BI] Fejl:', err.message);
 } finally {
 setLoading(false);
 }
 }, []);
 useEffect(() => {
 hent();
 const id = setInterval(hent, 30000);
 return () => clearInterval(id);
 }, [hent]);
 // ── Beregnede økonomiværdier ──────────────────────────
 const maanedTotal = oekData?.maaned_total || 0;
 const aarTotal = oekData?.aar_total || 0;
 const udestaaende = oekData?.udestaaende_total || 0;
 const forfaldne = oekData?.udestaaende_forfalden || 0;
 const forfaldneAntal = oekData?.udestaaende_forfalden_antal || 0;
 const mrr = oekData?.mrr || 0;
 const arr = oekData?.arr || 0;
 const loen = oekData?.loenforpligtelse_md || 60000;
 // ── Beregnede sagsværdier ─────────────────────────────
 const aktiveSager = sagData?.aktive_sager || 0;
 const nyeSager = sagData?.nye_denne_uge ?? 0;
 const lukkedeSager = sagData?.lukkede_sager ?? 0;
 const inaktive14d = sagData?.inaktive_14d ?? 0;
 const klienter = sagData?.total_klienter ?? 0;
 const h7d = sagData?.handlinger_7d ?? 0;
 const h30d = sagData?.handlinger_30d ?? 0;
 const aktivitetTrend = h30d > 0 ? Math.round(((h7d * 4.28) / h30d - 1) * 100) : 0;
 // ── Vækst-beregnede ──────────────────────────────────
 const totalAktive = vaekstData?.total_aktive_klienter || 0;
 const totalAfsluttede = vaekstData?.total_afsluttede_klienter || 0;
 const retentionPct = (totalAktive + totalAfsluttede) > 0
 ? (totalAktive / (totalAktive + totalAfsluttede)) * 100
 : 0;
 const nyeKlienterMd = vaekstData?.maanedlig?.[vaekstData.maanedlig.length - 1]?.nye_klienter ?? 0;
 // ── Real månedlig omsætningsgraf fra pr_maaned ────────
 const chartData = useMemo(() => {
 const pm = oekData?.pr_maaned || {};
 const entries = Object.entries(pm);
 if (entries.length === 0) return [];
 return entries
 .sort(([a], [b]) => a.localeCompare(b))
 .map(([k, v]) => ({
 name: MAANED_NAVNE[k.slice(5, 7)] || k,
 revenue: Math.round(v),
 }));
 }, [oekData]);
 // ── Vækstgraf data ────────────────────────────────────
 const vaekstChartData = useMemo(() => {
 return (vaekstData?.maanedlig || []).map(d => ({
 name: MAANED_NAVNE[String(d.maaned).slice(5, 7)] || d.maaned,
 nye_klienter: d.nye_klienter || 0,
 nye_sager: d.nye_sager || 0,
 }));
 }, [vaekstData]);
 // ── Sager pr fase ─────────────────────────────────────
 const faseData = useMemo(() => {
 const faser = sagData?.sager_pr_fase || {};
 const total = Object.values(faser).reduce((a, b) => a + b, 0) || 1;
 const maxVal = Math.max(...Object.values(faser), 1);
 return { faser, total, maxVal };
 }, [sagData]);
 const FASER_FARVER = [T.cyan, T.purple, T.blue, T.green, T.amber, T.gold, T.red];
 const FASE_LABELS = Object.keys(faseData.faser);
 // ── Tab-konfiguration ─────────────────────────────────
 const FANER = [
 { id: 'okonomi', label: 'Økonomi & Cashflow', ikon: '' },
 { id: 'vaekst', label: 'Vækst & Leads', ikon: '' },
 { id: 'marketing', label: 'Kampagner & ROI', ikon: '' },
 ];
 return (
 <> <style>{BI_CSS}</style> <div style={{ color: T.text, fontFamily: T.sans }}>
 {/* ══ HEADER ════════════════════════════════════════════ */}
 <div style={{
 display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
 marginBottom: 'clamp(16px, 2vw, 24px)', flexWrap: 'wrap', gap: 12,
 animation: 'bi-fade-up 0.4s ease both',
 }}> <div> <h1 style={{
 fontSize: 'clamp(22px, 2.8vw, 38px)', fontWeight: 900, margin: '0 0 6px',
 background: `linear-gradient(135deg, #ffffff 0%, ${T.cyan} 60%)`,
 WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
 letterSpacing: '-0.03em',
 }}>
 Business Intelligence
 </h1> <p style={{ color: T.text3, fontSize: 'clamp(11px, 1.1vw, 13px)', margin: 0, fontFamily: T.mono, letterSpacing: 0.5 }}>
 SkilsmisseVejen ApS · Realtidsdata · 30s auto-refresh
 {sidst && ` · Opdateret ${sidst.toLocaleTimeString('da-DK', { timeStyle: 'short' })}`}
 </p> </div> <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
 {beslutninger.length > 0 && (
 <div style={{
 background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.25)',
 borderRadius: 20, padding: '7px 16px', fontSize: 12, fontWeight: 700, color: T.amber,
 animation: 'bi-breathe 2s ease-in-out infinite',
 }}>
 {beslutninger.length} beslutning{beslutninger.length !== 1 ? 'er' : ''} afventer
 </div>
 )}
 <button onClick={hent} style={{
 background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10,
 padding: '7px 16px', color: T.text2, fontSize: 12, cursor: 'pointer', fontFamily: T.sans,
 }}>↺ Opdater</button> </div> </div>
 {/* ══ FANE-NAVIGATION ═══════════════════════════════════ */}
 <div style={{
 display: 'flex', gap: 4, marginBottom: 'clamp(18px, 2.4vw, 30px)',
 borderBottom: `1px solid ${T.border}`, paddingBottom: 0,
 animation: 'bi-fade-up 0.4s ease 0.05s both',
 overflowX: 'auto',
 }}>
 {FANER.map(fane => {
 const aktiv = aktiv_fane === fane.id;
 return (
 <button
 key={fane.id}
 onClick={() => setAktiv_fane(fane.id)}
 style={{
 background: 'transparent',
 border: 'none',
 borderBottom: aktiv ? `2px solid ${T.cyan}` : '2px solid transparent',
 color: aktiv ? T.cyan : T.text3,
 fontSize: 'clamp(12px, 1.2vw, 14px)',
 fontWeight: aktiv ? 700 : 500,
 fontFamily: T.sans,
 padding: '10px clamp(12px, 1.8vw, 22px)',
 cursor: 'pointer',
 whiteSpace: 'nowrap',
 marginBottom: -1,
 transition: 'color 0.2s ease, border-color 0.2s ease',
 display: 'flex', alignItems: 'center', gap: 8,
 }}
 > <span style={{ fontSize: 'clamp(13px, 1.3vw, 16px)' }}>{fane.ikon}</span>
 {fane.label}
 {fane.id === 'okonomi' && forfaldne > 0 && (
 <span style={{
 background: T.red, color: '#fff', borderRadius: 20,
 fontSize: 9, fontWeight: 800, padding: '1px 6px', lineHeight: 1.6,
 }}>!</span>
 )}
 </button>
 );
 })}
 </div>
 {/* ══ FANE INDHOLD ══════════════════════════════════════ */}
 <div key={aktiv_fane} style={{ animation: 'bi-tab-slide 0.3s ease both' }}>
 {/* ─────────────────────────────────────────────────── */}
 {/* FANE 1: ØKONOMI & CASHFLOW */}
 {/* ─────────────────────────────────────────────────── */}
 {aktiv_fane === 'okonomi' && (
 <>
 {/* Økonomi-integration ikke tilsluttet — handlingsanvisning */}
 {!loading && oekData == null && (
 <div style={{
 background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)',
 borderLeft: '4px solid rgba(245,158,11,0.6)',
 borderRadius: 14, padding: '16px 20px', marginBottom: 20,
 display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap',
 animation: 'bi-fade-up 0.35s ease both',
 }}> <span style={{ fontSize: 24, flexShrink: 0 }}></span> <div style={{ flex: 1, minWidth: 200 }}> <div style={{ fontWeight: 700, color: T.amber, fontSize: 14, marginBottom: 4 }}>
 Økonomi-integration ikke tilsluttet
 </div> <div style={{ fontSize: 13, color: T.text2, lineHeight: 1.55, marginBottom: 10 }}>
 Økonomidata hentes fra <strong style={{ color: T.text }}>Dinero</strong> via API. Data vises ikke fordi integrationen endnu ikke er sat op. Tal som 0 kr er <em>ikke</em> korrekte.
 </div> <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}> <a href="https://app.dinero.dk" target="_blank" rel="noopener noreferrer" style={{
 display: 'inline-flex', alignItems: 'center', gap: 6,
 background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)',
 borderRadius: 8, padding: '6px 14px', color: T.amber,
 fontSize: 12, fontWeight: 700, textDecoration: 'none', cursor: 'pointer',
 }}>
 Åbn Dinero →
 </a> <span style={{ fontSize: 12, color: T.text3, alignSelf: 'center' }}>
 Kontakt René for at opsætte Dinero API-nøgle i .env
 </span> </div> </div> </div>
 )}
 {/* 6 KPI-kort */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(140px, 15%, 250px), 1fr))',
 gap: 'clamp(10px, 1.3vw, 18px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <KPIKort label="MRR" value={loading ? null : oekData == null ? '—' : fmtKr(mrr)}
 farve={T.cyan} ikon="RefreshCw" sub={oekData == null ? 'Tilslut Dinero for data' : 'Månedligt tilbagevendende'}
 loading={loading} delay={0} /> <KPIKort label="Omsætning i år" value={loading ? null : oekData == null ? '—' : fmtKr(aarTotal)}
 farve={T.green} ikon="TrendingUp" sub={oekData == null ? 'Økonomi-integration afventer' : `${oekData?.aar_fakturaer || 0} fakturaer`}
 loading={loading} delay={0.06} /> <KPIKort label="Lønforpligtelse" value={loading ? null : (oekData == null ? '— Ikke tilsluttet' : maanedTotal >= loen ? ' Dækket' : ' Mangler')}
 farve={oekData == null ? T.text3 : maanedTotal >= loen ? T.green : T.red} ikon="Users"
 sub={oekData == null ? 'Økonomi-integration ikke sat op' : maanedTotal >= loen ? `${fmtKr(loen)}/md · Første prioritet` : `Mangler ${fmtKr(loen - maanedTotal)} · Gå til Dinero`}
 loading={loading} delay={0.12} /> <KPIKort label="Udestående" value={loading ? null : oekData == null ? '—' : fmtKr(udestaaende)}
 farve={oekData == null ? T.text3 : udestaaende > 0 ? T.amber : T.cyan} ikon="Hourglass"
 sub={oekData == null ? 'Ikke tilsluttet' : `${oekData?.udestaaende_antal || 0} fakturaer`}
 loading={loading} delay={0.18} /> <KPIKort label="Forfaldne" value={loading ? null : oekData == null ? '—' : fmtKr(forfaldne)}
 farve={oekData == null ? T.text3 : forfaldne > 0 ? T.red : T.green} ikon={oekData == null ? '—' : forfaldne > 0 ? '' : ''}
 sub={oekData == null ? 'Ikke tilsluttet' : forfaldne > 0 ? `${forfaldneAntal} fakturaer forfaldne` : 'Ingen forfaldne'}
 loading={loading} delay={0.24} /> <KPIKort label="ARR" value={loading ? null : oekData == null ? '—' : fmtKr(arr)}
 farve={oekData == null ? T.text3 : T.purple} ikon="Calendar" sub={oekData == null ? 'Ikke tilsluttet' : 'Årligt tilbagevendende'}
 loading={loading} delay={0.30} /> </div>
 {/* Lønforpligtelse + Revenue chart */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 45%, 700px), 1fr))',
 gap: 'clamp(14px, 1.8vw, 22px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <Panel accent={maanedTotal >= loen ? T.green : T.red} delay={0.1}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 20px', color: T.text }}>
 Lønforpligtelse — Første prioritet
 </h3>
 {loading
 ? <><Skel h={90} r={12} mb={12} /><Skel h={30} w="60%" /></>
 : <LoenRing maaned={maanedTotal} loensforpligtelse={loen} />}
 </Panel> <Panel accent={T.green} delay={0.15}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 16px', color: T.text }}>
 Månedlig omsætning {new Date().getFullYear()}
 </h3>
 {loading ? (
 <Skel h={160} r={12} />
 ) : chartData.length === 0 ? (
 <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}> <Skel h={160} r={12} /> </div>
 ) : (
 <ResponsiveContainer width="100%" height={160}> <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}> <defs> <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"> <stop offset="5%" stopColor={T.green} stopOpacity={0.3} /> <stop offset="95%" stopColor={T.green} stopOpacity={0.01} /> </linearGradient> </defs> <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.text3 }} axisLine={false} tickLine={false} /> <YAxis tick={{ fontSize: 10, fill: T.text3 }} axisLine={false} tickLine={false}
 tickFormatter={v => `${Math.round(v/1000)}k`} /> <Tooltip content={<CustomTooltip formatValue={fmtKr} />} /> <Area type="monotone" dataKey="revenue" stroke={T.green} strokeWidth={2}
 fill="url(#revGrad)" dot={{ r: 3, fill: T.green, strokeWidth: 0 }} /> </AreaChart> </ResponsiveContainer>
 )}
 </Panel> </div>
 {/* Scenarier + Udestående */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 45%, 700px), 1fr))',
 gap: 'clamp(14px, 1.8vw, 22px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <Panel accent={T.gold} delay={0.2}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 20px', color: T.text }}>
 Omsætnings-scenarier 2026
 </h3> <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}> <ScenarieBarre navn="Konservativt" interval="2,5M kr" farve={T.blue} 
 faktisk={aarTotal} maal={2500000} /> <ScenarieBarre navn="Realistisk" interval="3,5–4M kr" farve={T.green} 
 faktisk={aarTotal} maal={3750000} /> <ScenarieBarre navn="Ambitiøst" interval="5–6M kr" farve={T.purple} 
 faktisk={aarTotal} maal={5500000} /> </div> <div style={{
 marginTop: 16, padding: '10px 14px', borderRadius: 10,
 background: 'rgba(201,168,76,0.06)', border: `1px solid rgba(201,168,76,0.18)`,
 fontSize: 11, color: T.text2, fontStyle: 'italic',
 }}>
 Arbejdshypotese: Realistisk scenarie. B2B/B2G + Facebook-gruppe kan drive ambitiøst.
 </div> </Panel> <Panel accent={forfaldne > 0 ? T.red : T.amber} delay={0.25}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 18px', color: T.text }}>
 Udestående betalinger
 </h3>
 {loading ? [0,1,2].map(i => <Skel key={i} h={50} r={10} mb={12} />) : (
 <> <div style={{
 background: forfaldne > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)',
 border: `1px solid ${forfaldne > 0 ? 'rgba(239,68,68,0.20)' : 'rgba(245,158,11,0.20)'}`,
 borderRadius: 14, padding: '18px 20px', marginBottom: 14,
 }}> <div style={{ fontSize: 12, color: T.text3, marginBottom: 4, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 1 }}>Total udestående</div> <div style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 900, color: forfaldne > 0 ? T.red : T.amber, lineHeight: 1 }}>
 {fmtKr(udestaaende)}
 </div> </div>
 {forfaldne > 0 ? (
 <div style={{
 background: 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,0.20)`,
 borderRadius: 12, padding: '12px 16px', animation: 'bi-breathe 2s ease-in-out infinite',
 }}> <div style={{ fontSize: 11, color: T.red, fontWeight: 700, marginBottom: 3 }}> FORFALDNE ({forfaldneAntal} faktura{forfaldneAntal !== 1 ? 'er' : ''})</div> <div style={{ fontSize: 20, fontWeight: 800, color: T.red }}>{fmtKr(forfaldne)}</div> <div style={{ fontSize: 11, color: T.text2, marginTop: 3 }}>Kræver opfølgning</div> </div>
 ) : (
 <div style={{
 display: 'flex', alignItems: 'center', gap: 8,
 background: 'rgba(34,197,94,0.06)', border: `1px solid rgba(34,197,94,0.16)`,
 borderRadius: 10, padding: '10px 14px', fontSize: 12, color: T.green, fontWeight: 600,
 }}>
 Ingen forfaldne betalinger
 </div>
 )}
 </>
 )}
 </Panel> </div> </>
 )}
 {/* ─────────────────────────────────────────────────── */}
 {/* FANE 2: VÆKST & LEADS */}
 {/* ─────────────────────────────────────────────────── */}
 {aktiv_fane === 'vaekst' && (
 <> <div style={{ marginBottom: 'clamp(16px, 2vw, 24px)', animation: 'bi-fade-up 0.35s ease both' }}> <h2 style={{ fontSize: 'clamp(17px, 2vw, 26px)', fontWeight: 800, margin: '0 0 4px', color: T.text }}>
 Vækst & Klienter
 </h2> <p style={{ color: T.text3, fontSize: 12, margin: 0, fontFamily: T.mono }}>
 Klienttilgang, fastholdelse og sags-pipeline
 </p> </div>
 {/* 4 KPI-kort */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(140px, 20%, 260px), 1fr))',
 gap: 'clamp(10px, 1.3vw, 18px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <KPIKort label="Nye klienter (md.)" value={loading ? null : fmtNum(nyeKlienterMd)}
 farve={T.cyan} sub="Seneste måned"
 loading={loading} delay={0} /> <KPIKort label="Nye sager (uge)" value={loading ? null : fmtNum(nyeSager)}
 farve={T.purple} ikon="ClipboardList" sub="Denne uge"
 loading={loading} delay={0.06} /> <KPIKort label="Aktive klienter" value={loading ? null : fmtNum(totalAktive || klienter)}
 farve={T.green} ikon="Users" sub="I aktive forløb"
 loading={loading} delay={0.12} /> <KPIKort label="Inaktive >14 dage" value={loading ? null : fmtNum(inaktive14d)}
 farve={inaktive14d > 0 ? T.red : T.green} ikon={inaktive14d > 0 ? '' : ''}
 sub={inaktive14d > 0 ? 'Kræver opfølgning' : 'Alle sager aktive'}
 loading={loading} delay={0.18} /> </div>
 {/* Vækstgraf + Fastholdelsesring */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 55%, 700px), 1fr))',
 gap: 'clamp(14px, 1.8vw, 22px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <Panel accent={T.cyan} delay={0.1}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 16px', color: T.text }}>
 Månedlig vækst — Klienter & Sager
 </h3>
 {loading ? (
 <Skel h={180} r={12} />
 ) : vaekstChartData.length === 0 ? (
 <div style={{
 height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center',
 color: T.text3, fontSize: 13,
 }}>
 Ingen vækstdata tilgængelig endnu
 </div>
 ) : (
 <ResponsiveContainer width="100%" height={180}> <BarChart data={vaekstChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barGap={4}> <XAxis dataKey="name" tick={{ fontSize: 10, fill: T.text3 }} axisLine={false} tickLine={false} /> <YAxis tick={{ fontSize: 10, fill: T.text3 }} axisLine={false} tickLine={false} allowDecimals={false} /> <Tooltip content={<CustomTooltip />} /> <Bar dataKey="nye_klienter" name="Nye klienter" fill={T.cyan} radius={[4,4,0,0]}>
 {vaekstChartData.map((_, i) => <Cell key={i} fill={T.cyan} fillOpacity={0.85} />)}
 </Bar> <Bar dataKey="nye_sager" name="Nye sager" fill={T.purple} radius={[4,4,0,0]}>
 {vaekstChartData.map((_, i) => <Cell key={i} fill={T.purple} fillOpacity={0.85} />)}
 </Bar> </BarChart> </ResponsiveContainer>
 )}
 <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
 {[{ farve: T.cyan, label: 'Nye klienter' }, { farve: T.purple, label: 'Nye sager' }].map(item => (
 <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}> <div style={{ width: 10, height: 10, borderRadius: 3, background: item.farve }} /> <span style={{ fontSize: 11, color: T.text3 }}>{item.label}</span> </div>
 ))}
 </div> </Panel> <Panel accent={T.green} delay={0.15}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 20px', color: T.text }}>
 Fastholdelse & pipeline
 </h3>
 {loading ? (
 <><Skel h={100} w={100} r={50} mb={12} /><Skel h={30} w="60%" /></>
 ) : (
 <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}> <GaugeRing
 pct={retentionPct}
 farve={retentionPct > 70 ? T.green : retentionPct > 40 ? T.amber : T.red}
 label="Fastholdelse"
 sublabel="aktive"
 /> <div style={{ flex: 1, minWidth: 160 }}> <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
 {[
 { label: 'Aktive klienter', value: totalAktive || klienter, farve: T.green },
 { label: 'Afsluttede forløb', value: totalAfsluttede, farve: T.text2 },
 { label: 'Aktive sager', value: aktiveSager, farve: T.cyan },
 { label: 'Lukkede sager', value: lukkedeSager, farve: T.text3 },
 ].map(({ label, value, farve }) => (
 <div key={label} style={{
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 padding: '8px 12px', background: 'rgba(255,255,255,0.03)',
 borderRadius: 10, border: `1px solid ${T.border}`,
 }}> <span style={{ fontSize: 12, color: T.text2 }}>{label}</span> <span style={{ fontSize: 15, fontWeight: 800, color: farve }}>{fmtNum(value)}</span> </div>
 ))}
 </div> </div> </div>
 )}
 </Panel> </div>
 {/* Facebook gruppe + Sager pr. fase */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 45%, 700px), 1fr))',
 gap: 'clamp(14px, 1.8vw, 22px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <Panel accent="#4267B2" delay={0.2}> <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: 0, color: T.text }}>
 Facebook-gruppe
 </h3> <div style={{
 background: T.amber, color: '#000',
 borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
 animation: 'bi-breathe 3s ease-in-out infinite',
 }}>
 TOPPRIORITET
 </div> </div> <div style={{
 background: 'rgba(66,103,178,0.10)', border: '1px solid rgba(66,103,178,0.25)',
 borderRadius: 16, padding: '20px 22px', marginBottom: 14,
 }}> <div style={{ fontSize: 11, color: T.text3, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
 Skilsmisse & Samvær
 </div> <div style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 900, color: '#4267B2', lineHeight: 1, textShadow: '0 0 24px rgba(66,103,178,0.4)' }}>
 3.000
 </div> <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>registrerede medlemmer</div> </div> <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
 {[
 { label: 'Mål: ugentlig aktivitet', value: '30%+', farve: T.amber, note: 'nuværende: —' },
 { label: 'Konverteringsmål', value: 'Gruppe → Klient', farve: T.cyan, note: 'ikke målt endnu' },
 { label: 'Strategisk status', value: 'Aktiv reaktivering', farve: T.green, note: 'højeste prioritet' },
 ].map(({ label, value, farve, note }) => (
 <div key={label} style={{
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 padding: '9px 13px', background: `${farve}08`,
 border: `1px solid ${farve}18`, borderRadius: 10,
 }}> <div> <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{label}</div> <div style={{ fontSize: 10, color: T.text3 }}>{note}</div> </div> <span style={{ fontSize: 12, fontWeight: 800, color: farve }}>{value}</span> </div>
 ))}
 </div> </Panel> <Panel accent={T.blue} delay={0.25}> <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: 0, color: T.text }}>
 Sager pr. fase
 </h3> <div style={{ textAlign: 'right' }}> <div style={{ fontSize: 'clamp(22px, 2.5vw, 32px)', fontWeight: 900, color: T.blue, lineHeight: 1 }}>{aktiveSager}</div> <div style={{ fontSize: 10, color: T.text3, fontFamily: T.mono }}>aktive sager</div> </div> </div>
 {loading ? (
 [0,1,2,3].map(i => <Skel key={i} h={40} r={8} mb={12} />)
 ) : FASE_LABELS.length === 0 ? (
 <div style={{ textAlign: 'center', padding: '24px 0', color: T.text3, fontSize: 13 }}>
 Ingen fasedata tilgængelig
 </div>
 ) : (
 FASE_LABELS.map((fase, i) => (
 <HorisontalBar
 key={fase} label={fase}
 value={faseData.faser[fase]} max={faseData.maxVal}
 total={aktiveSager}
 farve={FASER_FARVER[i % FASER_FARVER.length]}
 />
 ))
 )}
 </Panel> </div> </>
 )}
 {/* ─────────────────────────────────────────────────── */}
 {/* FANE 3: KAMPAGNER & MARKETING */}
 {/* ─────────────────────────────────────────────────── */}
 {aktiv_fane === 'marketing' && (
 <> <div style={{ marginBottom: 'clamp(16px, 2vw, 24px)', animation: 'bi-fade-up 0.35s ease both' }}> <h2 style={{ fontSize: 'clamp(17px, 2vw, 26px)', fontWeight: 800, margin: '0 0 4px', color: T.text }}>
 Kampagner & ROI
 </h2> <p style={{ color: T.text3, fontSize: 12, margin: 0, fontFamily: T.mono }}>
 Annoncering, organisk vækst og synlighed
 </p> </div>
 {/* 4 KPI-kort */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(140px, 20%, 260px), 1fr))',
 gap: 'clamp(10px, 1.3vw, 18px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <KPIKort label="Kampagnebudget" value="—"
 farve={T.text3} ikon="Banknote" sub="Ikke forbundet endnu"
 loading={false} delay={0} /> <KPIKort label="Brug til dato" value="—"
 farve={T.text3} ikon="TrendingDown" sub="Afventer Meta API"
 loading={false} delay={0.06} /> <KPIKort label="Estimeret ROI" value="—"
 farve={T.text3} ikon="BarChart2" sub="Mål: ROAS > 3x"
 loading={false} delay={0.12} /> <KPIKort label="Leads fra kampagner" value="—"
 farve={T.text3} ikon="Target" sub="Kobles til Analytics"
 loading={false} delay={0.18} /> </div>
 {/* Meta Ads status + ROI-kalkulator note */}
 <div style={{
 display: 'grid',
 gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(280px, 45%, 700px), 1fr))',
 gap: 'clamp(14px, 1.8vw, 22px)',
 marginBottom: 'clamp(16px, 2vw, 26px)',
 }}> <Panel accent={T.text3} delay={0.1}> <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}> <div style={{
 width: 42, height: 42, borderRadius: 12,
 background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: 22,
 }}></div> <div> <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: T.text }}>Meta Ads API</h3> <div style={{
 display: 'inline-flex', alignItems: 'center', gap: 6,
 background: 'rgba(74,90,114,0.15)', border: `1px solid ${T.text3}30`,
 borderRadius: 20, padding: '2px 10px', marginTop: 4,
 fontSize: 10, fontWeight: 700, color: T.text3,
 }}> <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.text3 }} />
 IKKE FORBUNDET
 </div> </div> </div> <div style={{
 background: 'rgba(255,255,255,0.03)', border: `1px solid ${T.border}`,
 borderRadius: 14, padding: '18px 20px', marginBottom: 16,
 }}> <p style={{ color: T.text2, fontSize: 13, lineHeight: 1.65, margin: 0 }}>
 Meta Ads API er ikke forbundet endnu. Kobles til når René opretter Meta Business API-adgang.
 Herefter vises kampagne-performance, ROAS og lead-data automatisk i dette dashboard.
 </p> </div> <button disabled style={{
 width: '100%', padding: '11px 0',
 background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}`,
 borderRadius: 10, color: T.text3, fontSize: 13, fontWeight: 600,
 cursor: 'not-allowed', fontFamily: T.sans,
 }}>
 Forbind Meta Business API →
 </button> </Panel> <Panel accent={T.cyan} delay={0.15}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: '0 0 6px', color: T.text }}>
 SEO & AEO synlighed
 </h3> <p style={{ fontSize: 12, color: T.text3, margin: '0 0 16px', fontFamily: T.mono }}>
 Organisk trafik og AI-søgesynlighed
 </p> <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
 {[
 {
 ikon: '', label: 'GA4 — Organisk trafik',
 status: 'Under opsætning', farve: T.amber,
 note: 'Google Analytics 4 integration planlagt',
 },
 {
 ikon: '', label: 'LLM-synlighed (AEO)',
 status: 'Ugentlig analyse', farve: T.cyan,
 note: 'marketing-agenten analyserer citationer i ChatGPT, Perplexity m.fl.',
 },
 {
 ikon: '', label: 'Keyword rankings',
 status: 'Ikke tilsluttet', farve: T.text3,
 note: 'Kobles via Search Console API',
 },
 ].map(({ ikon, label, status, farve, note }) => (
 <div key={label} style={{
 display: 'flex', gap: 12, alignItems: 'flex-start',
 padding: '12px 14px', background: `${farve}06`,
 border: `1px solid ${farve}18`, borderRadius: 12,
 }}> <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{ikon}</span> <div style={{ flex: 1, minWidth: 0 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}> <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{label}</span> <span style={{ fontSize: 10, fontWeight: 700, color: farve }}>{status}</span> </div> <div style={{ fontSize: 11, color: T.text3, marginTop: 3 }}>{note}</div> </div> </div>
 ))}
 </div> </Panel> </div>
 {/* Kampagne-oversigt tabel */}
 <Panel accent={T.purple} delay={0.2} style={{ marginBottom: 'clamp(14px, 1.8vw, 22px)' }}> <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 8 }}> <h3 style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', fontWeight: 700, margin: 0, color: T.text }}>
 Kampagne-oversigt
 </h3> <div style={{
 background: 'rgba(139,92,246,0.10)', border: `1px solid rgba(139,92,246,0.20)`,
 borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700, color: T.purple,
 }}>
 Meta Ads API ikke forbundet
 </div> </div> <div style={{ overflowX: 'auto' }}> <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}> <thead> <tr>
 {['Kampagne', 'Status', 'Budget', 'Brug', 'Leads', 'ROI'].map(col => (
 <th key={col} style={{
 textAlign: 'left', padding: '10px 14px',
 color: T.text3, fontSize: 11, fontFamily: T.mono, fontWeight: 700,
 textTransform: 'uppercase', letterSpacing: 0.8,
 borderBottom: `1px solid ${T.border}`,
 }}>
 {col}
 </th>
 ))}
 </tr> </thead> <tbody>
 {[
 { kampagne: 'Facebook Gruppe Boost', status: 'Opsætning pågår', budget: '—', brug: '—', leads: '—', roi: '—' },
 { kampagne: 'Google Søgeannonce — Skilsmisse', status: 'Opsætning pågår', budget: '—', brug: '—', leads: '—', roi: '—' },
 { kampagne: 'LinkedIn B2B', status: 'Opsætning pågår', budget: '—', brug: '—', leads: '—', roi: '—' },
 ].map((row, i) => (
 <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}> <td style={{ padding: '12px 14px', color: T.text, fontWeight: 600 }}>{row.kampagne}</td> <td style={{ padding: '12px 14px' }}> <span style={{
 background: 'rgba(139,92,246,0.10)', border: `1px solid rgba(139,92,246,0.20)`,
 borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: T.purple,
 }}>
 {row.status}
 </span> </td>
 {[row.budget, row.brug, row.leads, row.roi].map((v, j) => (
 <td key={j} style={{ padding: '12px 14px', color: T.text3, fontFamily: T.mono }}>{v}</td>
 ))}
 </tr>
 ))}
 </tbody> </table> </div> <div style={{
 marginTop: 16, padding: '12px 16px', borderRadius: 10,
 background: 'rgba(139,92,246,0.06)', border: `1px solid rgba(139,92,246,0.15)`,
 fontSize: 12, color: T.text2, display: 'flex', alignItems: 'flex-start', gap: 8,
 }}> <span style={{ fontSize: 16, flexShrink: 0 }}></span> <span> <strong style={{ color: T.text }}>ROI-kalkulator:</strong>{' '}
 Kobles til Meta Ads API + Google Analytics for automatisk ROI-beregning. Mål: ROAS &gt; 3x.
 Alle tre kampagner er i opsætningsfasen — data vises automatisk når API-forbindelser er aktive.
 </span> </div> </Panel> </>
 )}
 </div>{/* end tab content */}
 {/* ══ FOOTER-NOTE ════════════════════════════════════════ */}
 <div style={{
 background: T.surface, border: `1px solid ${T.border}`,
 borderRadius: 14, padding: 'clamp(12px, 1.5vw, 18px) clamp(16px, 2vw, 24px)',
 display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
 animation: 'bi-fade-up 0.4s ease 0.5s both',
 marginTop: 'clamp(14px, 1.8vw, 22px)',
 }}> <span style={{ fontSize: 16 }}></span> <div> <span style={{ color: T.text2, fontSize: 12, fontWeight: 600 }}>Månedlig rapport</span> <span style={{ color: T.text3, fontSize: 12 }}> genereres automatisk første mandag i måneden · </span> <span style={{ color: T.text2, fontSize: 12, fontWeight: 600 }}>Økonomidata</span> <span style={{ color: T.text3, fontSize: 12 }}> hentes direkte fra Dinero via API · </span> <span style={{ color: T.text2, fontSize: 12, fontWeight: 600 }}>Vækstdata</span> <span style={{ color: T.text3, fontSize: 12 }}> hentes fra sagsportalen</span> </div> <div style={{ marginLeft: 'auto', fontSize: 10, color: T.text3, fontFamily: T.mono }}>
 v2.0 · BI Dashboard · Tabbed
 </div> </div> </div> </>
 );
}